import { EtJournalEntry, EtJournalSigner } from '@et/typings';
import { Selector } from '@ngxs/store';
import { EjournalSessionModel } from './ejournal-session.model';
import { EjournalSessionState } from './ejournal-session.state';

function parseEntryIds(raw: string): string[] {
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

function isEntryAssigned(
  entry: EtJournalEntry,
  signers: EtJournalSigner[],
): boolean {
  return signers.some((signer) =>
    parseEntryIds(signer.ejournalEntryIds).includes(entry.eJournalEntryId),
  );
}

export class EjournalSessionSelectors {
  @Selector([EjournalSessionState])
  static getProfile(state: EjournalSessionModel) {
    return state.profile;
  }

  @Selector([EjournalSessionState])
  static getSelectedPacket(state: EjournalSessionModel) {
    return state.selectedPacket;
  }

  @Selector([EjournalSessionState])
  static getEjournal(state: EjournalSessionModel) {
    return state.eJournal;
  }

  @Selector([EjournalSessionState])
  static getSigners(state: EjournalSessionModel) {
    return state.signers;
  }

  @Selector([EjournalSessionState])
  static getEntries(state: EjournalSessionModel) {
    return state.entries;
  }

  /** Unassigned non-cancel-act entries (seal holder placeholders). */
  @Selector([
    EjournalSessionSelectors.getEntries,
    EjournalSessionSelectors.getSigners,
  ])
  static getSealEntries(entries: EtJournalEntry[], signers: EtJournalSigner[]) {
    return entries.filter(
      (e) => !e.cancelActUtcDate && !isEntryAssigned(e, signers),
    );
  }

  /** Entries linked to at least one signer. */
  @Selector([
    EjournalSessionSelectors.getEntries,
    EjournalSessionSelectors.getSigners,
  ])
  static getCleanEntries(
    entries: EtJournalEntry[],
    signers: EtJournalSigner[],
  ) {
    return entries.filter((e) => isEntryAssigned(e, signers));
  }

  @Selector([EjournalSessionState])
  static getNotarizedAuditActions(state: EjournalSessionModel) {
    return state.notarizedAuditActions;
  }

  @Selector([EjournalSessionState])
  static getNotarizedAuditLoaded(state: EjournalSessionModel) {
    return state.notarizedAuditLoaded;
  }

  @Selector([EjournalSessionState])
  static getPageNamesByNumber(state: EjournalSessionModel) {
    return state.pageNamesByNumber;
  }

  @Selector([EjournalSessionState])
  static getEjournalLoading(state: EjournalSessionModel) {
    return state.eJournalLoading;
  }

  @Selector([EjournalSessionState])
  static getSignersLoading(state: EjournalSessionModel) {
    return state.signersLoading;
  }

  @Selector([EjournalSessionState])
  static getEntriesLoading(state: EjournalSessionModel) {
    return state.entriesLoading;
  }

  @Selector([EjournalSessionState])
  static getEjournalError(state: EjournalSessionModel) {
    return state.eJournalError;
  }

  @Selector([EjournalSessionState])
  static getSignersError(state: EjournalSessionModel) {
    return state.signersError;
  }

  @Selector([EjournalSessionState])
  static getEntriesError(state: EjournalSessionModel) {
    return state.entriesError;
  }

  @Selector([EjournalSessionState])
  static getIp(state: EjournalSessionModel) {
    return state.ip;
  }

  @Selector([EjournalSessionState])
  static getHasSelectedPacket(state: EjournalSessionModel) {
    return state.selectedPacket !== null;
  }

  @Selector([EjournalSessionState])
  static getHasEjournal(state: EjournalSessionModel) {
    return state.eJournal !== null;
  }

  @Selector([EjournalSessionState])
  static getIsFinalized(state: EjournalSessionModel) {
    return !!state.eJournal?.isFinal;
  }

  @Selector([EjournalSessionState])
  static getHasSigners(state: EjournalSessionModel) {
    return state.signers.length > 0;
  }

  @Selector([EjournalSessionState])
  static getHasEntries(state: EjournalSessionModel) {
    return state.entries.length > 0;
  }

  @Selector([EjournalSessionState])
  static getIsAnyLoading(state: EjournalSessionModel) {
    return (
      state.eJournalLoading || state.signersLoading || state.entriesLoading
    );
  }
}
